import React, { useState, useEffect } from 'react';
import api from '../services/api';

/**
 * StandaloneSeriesModal — shared modal for linking a transaction to a standalone recurring series.
 *
 * Props:
 * - isOpen: boolean
 * - row: { id, sourceType: 'revolver'|'cash'|'property'|'vehicle', expense_category, standalone_recurring_series_id, standalone_recurring_series_name }
 * - onClose: () => void
 * - onSaveSuccess: () => void
 */
const StandaloneSeriesModal = ({ isOpen, row, onClose, onSaveSuccess }) => {
  const [mode, setMode] = useState('existing');
  const [existingOptions, setExistingOptions] = useState([]);
  const [selectedSeriesId, setSelectedSeriesId] = useState('');
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('fixed');
  const [newFrequency, setNewFrequency] = useState('monthly');
  const [newVendor, setNewVendor] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !row) return;
    setMode('existing');
    setSelectedSeriesId(row.standalone_recurring_series_id ? String(row.standalone_recurring_series_id) : '');
    setNewName('');
    setNewType('fixed');
    setNewFrequency('monthly');
    setNewVendor('');
    setError('');
    setLoadingOptions(true);
    const params = row.expense_category ? `?category=${row.expense_category}` : '';
    api.get(`/balance_sheet/assets/standalone-recurring-series/${params}`)
      .then(res => {
        const all = Array.isArray(res.data) ? res.data : [];
        if (all.length === 0 && row.expense_category) {
          return api.get('/balance_sheet/assets/standalone-recurring-series/').then(r => setExistingOptions(Array.isArray(r.data) ? r.data : []));
        }
        setExistingOptions(all);
      })
      .catch(() => setExistingOptions([]))
      .finally(() => setLoadingOptions(false));
  }, [isOpen, row]);

  if (!isOpen || !row) return null;

  const getTransactionEndpoint = () => {
    const src = row.sourceType || 'cash';
    if (src === 'revolver') return `/balance_sheet/financing/revolvers/transactions/${row.id}/`;
    return `/balance_sheet/assets/cash_accounts/transactions/${row.id}/`;
  };

  const handleSave = async () => {
    setError('');
    setLoading(true);
    try {
      let seriesId = null;
      if (mode === 'existing') {
        if (!selectedSeriesId) { setError('Please select an existing series.'); setLoading(false); return; }
        seriesId = Number(selectedSeriesId);
      } else {
        if (!newName.trim()) { setError('Please enter a series name.'); setLoading(false); return; }
        const res = await api.post('/balance_sheet/assets/standalone-recurring-series/', {
          display_name: newName.trim(),
          expense_category: row.expense_category || null,
          recurring_type: newType,
          recurring_frequency: newFrequency,
          vendor_payee: newVendor.trim() || null,
        });
        seriesId = res.data.id;
      }
      await api.patch(getTransactionEndpoint(), { standalone_recurring_series: seriesId });
      onSaveSuccess();
    } catch (e) {
      setError('Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlink = async () => {
    setError('');
    setLoading(true);
    try {
      await api.patch(getTransactionEndpoint(), { standalone_recurring_series: null });
      onSaveSuccess();
    } catch (e) {
      setError('Failed to unlink. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2200 }}
      onClick={() => { if (!loading) onClose(); }}
    >
      <div
        style={{ width: 'min(520px,95vw)', background: '#0b1220', border: '1px solid #374151', borderRadius: 8, color: '#e5e7eb' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #374151', display: 'flex', alignItems: 'center' }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>Link Recurring Series</div>
          <button className="btn btn-sm btn-secondary" onClick={onClose} disabled={loading} style={{ marginLeft: 'auto' }}>✕</button>
        </div>
        <div style={{ padding: 16 }}>
          {error && <div className="alert alert-danger py-2" style={{ background: 'transparent', color: '#f87171', borderColor: '#7f1d1d' }}>{error}</div>}
          <div style={{ marginBottom: 12, color: '#9ca3af', fontSize: '0.85rem' }}>
            Category: <span style={{ color: '#e5e7eb' }}>{row.expense_category || '—'}</span>
            {row.standalone_recurring_series_name && (
              <span style={{ marginLeft: 12 }}>Current: <span style={{ color: '#10b981' }}>{row.standalone_recurring_series_name}</span></span>
            )}
          </div>
          <div className="btn-group mb-3" style={{ width: '100%' }}>
            <button className={`btn btn-sm ${mode === 'existing' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setMode('existing')}>Existing</button>
            <button className={`btn btn-sm ${mode === 'new' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setMode('new')}>New</button>
          </div>
          {mode === 'existing' ? (
            <div>
              <label className="form-label">Select Series</label>
              <select className="form-select" value={selectedSeriesId} onChange={e => setSelectedSeriesId(e.target.value)} disabled={loadingOptions}>
                <option value="">{loadingOptions ? 'Loading…' : '— Select series —'}</option>
                {existingOptions.map(s => (
                  <option key={s.id} value={String(s.id)}>{s.display_name} ({s.recurring_type} / {s.recurring_frequency})</option>
                ))}
              </select>
              {!loadingOptions && existingOptions.length === 0 && (
                <small className="text-muted">No existing series found. Switch to "New" to create one.</small>
              )}
            </div>
          ) : (
            <div>
              <div className="mb-2">
                <label className="form-label">Series Name *</label>
                <input className="form-control" value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Netflix, Verizon Bill" />
              </div>
              <div className="mb-2">
                <label className="form-label">Type</label>
                <select className="form-select" value={newType} onChange={e => setNewType(e.target.value)}>
                  <option value="fixed">Fixed</option>
                  <option value="variable">Variable</option>
                </select>
              </div>
              <div className="mb-2">
                <label className="form-label">Frequency</label>
                <select className="form-select" value={newFrequency} onChange={e => setNewFrequency(e.target.value)}>
                  <option value="weekly">Weekly</option>
                  <option value="bi_weekly">Bi-Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annually">Annually</option>
                </select>
              </div>
              <div className="mb-2">
                <label className="form-label">Vendor/Payee</label>
                <input className="form-control" value={newVendor} onChange={e => setNewVendor(e.target.value)} placeholder="Optional" />
              </div>
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center" style={{ marginTop: 16 }}>
            <div>
              {row.standalone_recurring_series_id && (
                <button className="btn btn-outline-danger btn-sm" onClick={handleUnlink} disabled={loading}>Unlink</button>
              )}
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={loading}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={loading}>
                {loading ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StandaloneSeriesModal;
